import { validatePhoneNumberLength } from 'libphonenumber-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { useToastState } from '~/atoms/ToastState'
import { FontStyle } from '~/components/core/Text'
import { MangoButton } from '~/components/shared/MangoButton'
import { ContactSearchInput } from '~/components/specialized/ContactSearchInput/ContactSearchInput'
import { FILESIZE, MMSTypes } from '~/constants/config'
import { spacing } from '~/constants/theme'
import urls from '~/constants/urls'
import { useAnalytics } from '~/hooks/useAnalytics'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import MessageInput from '~/pages/Messaging/MessageInput'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { EmojiData } from '~/types/EmojiMartTypes'
import {
  fileToBase64,
  formatPhoneNumber,
  getDefaultCountryCode,
  getValidNumber,
} from '~/utils/formatter'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { ContactSourceType, MessageGroup } from '@mangovoice/types'

import { Text } from './core'
import { DropdownSelect } from './shared/Dropdown/DropdownSelect'
import { MangoModal } from './shared/MangoModal'

// Module-level so the reference is stable and doesn't defeat ContactSearchInput's memo.
const CONTACT_SOURCE_TYPE_FILTER = [ContactSourceType.Extensions]

const ModalBodyWrapper = styled.div`
  width: 100%;
  margin-bottom: ${spacing.spacingMD};
`

const ModalDetailWrapper = styled.div`
  width: 100%;
`

const ContactAdminWrapper = styled.div`
  box-sizing: border-box;

  > p {
    margin-bottom: ${spacing.spacingXXL};
  }
`

const MangoATag = styled.a`
  text-decoration: none;
  color: var(--chakra-colors-brand-emphasized);

  &:hover {
    text-decoration: underline;
    color: var(--chakra-colors-brand-emphasized);
  }
`

const Margin = styled.div`
  margin-bottom: ${spacing.spacingMD};
`

const NewThreadModal = () => {
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
  const [msgFile, setMsgFile] = useState<File | undefined>()
  const [fileUrl, setFileUrl] = useState<string>()
  const {
    messagingModal: [messagingModalState, setMessagingModalState],
  } = useModalState()

  const { t } = useTranslation()

  const { userMessageGroups, tryFetchUserMessageGroups } =
    useMessageGroupsViewModel()
  const groups = userMessageGroups ?? []

  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<MessageGroup>()
  const [phone, setPhone] = useState('')
  const [msgText, setMsgText] = useState('')

  const { tryGetOrCreateThread } = useMessageThreadsViewModel()
  const { hasExceededMaxCount } = useMaxCharacterCount(msgText.length)

  const {
    messagingToast: [, setMessagingToastState],
  } = useToastState()

  //on mount, try and fetch the latest message groups from the view model
  useEffect(() => {
    tryFetchUserMessageGroups()
  }, [])

  useEffect(() => {
    if (!selectedGroup && groups.length > 0) {
      setSelectedGroup(groups[0])
    }
  }, [groups, selectedGroup])

  const handleSelectGroup = (selectedId: number) => {
    const group = groups.find((g) => g.id === selectedId)
    setSelectedGroup(group)
    track(MessagingAnalyticsEvents.NewThreadModalGroupSelected, {
      group_id: group?.id ?? 0,
      name: group?.name ?? '',
    })
  }

  const handleChangePhone = (newPhone: string) => {
    const pNum = getValidNumber(newPhone)
    // Allow international numbers (E.164 max is 15 digits, +1 for '+' prefix)
    if (pNum.length <= 16) {
      setPhone(newPhone)
    }
  }

  const onDrop: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => Promise<void> = useCallback(async (acceptedFiles, fileRejections) => {
    if (acceptedFiles[0]) {
      const dataURL = (await fileToBase64(acceptedFiles[0])) as string
      setMsgFile(acceptedFiles[0])
      setFileUrl(dataURL)
    }
    if (fileRejections[0]) {
      const errorCode = fileRejections[0].errors[0].code

      setMessagingToastState((currentMessagingState) => {
        return {
          ...currentMessagingState,
          messagingStatus: errorCode,
        }
      })
    }

    setIsOpenDialog(false)
  }, [])
  const onFileDialogCancel = () => {
    setIsOpenDialog(false)
  }

  const isDraggable = !msgFile
  const { open, getInputProps } = useDropzone({
    accept: MMSTypes,
    disabled: !isDraggable,
    maxSize: FILESIZE.MAX_MMS,
    noDrag: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onFileDialogCancel,
  })

  const handleOpenAttach = () => {
    open()
    setIsOpenDialog(true)
  }

  const handleChangeMsgText = useCallback(
    (text: string | undefined) => setMsgText(text ?? ''),
    [],
  )

  const handleSelectEmoji = (emoji: EmojiData) => {
    setMsgText((prevText) => prevText + emoji.native)
  }

  const handleCloseModal = () => {
    setSelectedGroup(groups[0])
    setPhone('')
    setMsgText('')
    setMessagingModalState({ open: false })
    setFileUrl(undefined)
    setMsgFile(undefined)
  }

  const handleClearInput = () => {
    setMsgText('')
    setFileUrl(undefined)
    setMsgFile(undefined)
  }

  const handleCreateNewThread = () => {
    const pNum = getValidNumber(phone)

    tryGetOrCreateThread({
      groupId: selectedGroup?.id ?? 0,
      externalPhoneNumber: pNum,
      name: pNum,
      message: {
        body: msgText,
        file: msgFile,
      },
    })
    track(MessagingAnalyticsEvents.NewThreadModalSendPressed)

    handleCloseModal()
  }

  const displayValue = (group: MessageGroup) => {
    return ` ${group.name} - ${
      group.phone_numbers.find((number) => number.is_primary_sms)
        ?.number_for_display ?? ''
    }`
  }

  if (messagingModalState.number && phone !== messagingModalState.number) {
    setPhone(formatPhoneNumber(messagingModalState.number))
    setMessagingModalState({ ...messagingModalState, number: '' })
  }

  const pNum = getValidNumber(phone)
  const disabled =
    pNum.length < 10 ||
    (msgText === '' && msgFile === undefined) ||
    !selectedGroup ||
    hasExceededMaxCount
  const noGroups = groups.length === 0

  const recipientFieldRef = useRef<HTMLDivElement>(null)
  const getRecipientInput = () =>
    recipientFieldRef.current?.querySelector<HTMLElement>('input') ?? null

  const isToNumberInvalid = Boolean(
    validatePhoneNumberLength(pNum ?? '', {
      defaultCountry: getDefaultCountryCode(),
    }),
  )
  return (
    <MangoModal
      open={messagingModalState.open}
      initialFocusEl={getRecipientInput}
      header={
        noGroups
          ? t('newThreadModal.header.noGroupFound')
          : t('newThreadModal.header.newThread')
      }
      footer={
        <MangoButton disabled={disabled} onClick={handleCreateNewThread}>
          {t('newThreadModal.sendButtonText')}
        </MangoButton>
      }
      onOpenChange={(details) => {
        if (!details.open) handleCloseModal()
      }}
    >
      {noGroups ? (
        <ModalBodyWrapper>
          <ContactAdminWrapper>
            <Text
              align="left"
              fontStyle={FontStyle.Normal}
              text={t('newThreadModal.contactText1')}
            />
            <Text
              align="left"
              fontStyle={FontStyle.Normal}
              text={t('newThreadModal.contactText2', {
                adminUrl: urls.ADMIN.replace('https://', ''),
              })}
            />
            <MangoATag
              href="https://support.mangovoice.com/what-is-a-messaging-group"
              target="_blank"
            >
              {t('newThreadModal.contactLinkText')}
            </MangoATag>
          </ContactAdminWrapper>
        </ModalBodyWrapper>
      ) : (
        <ModalBodyWrapper>
          <ModalDetailWrapper>
            <Margin>
              <DropdownSelect
                items={groups.map((rawGroup) => ({
                  value: rawGroup.id,
                  content: displayValue(rawGroup),
                }))}
                selectedValue={selectedGroup?.id}
                onSelect={(selectedId) => handleSelectGroup(selectedId)}
              />
            </Margin>
            <Margin ref={recipientFieldRef}>
              <ContactSearchInput
                isInvalid={isToNumberInvalid}
                value={phone}
                onValueChanged={handleChangePhone}
                contactSourceTypeFilter={CONTACT_SOURCE_TYPE_FILTER}
              />
            </Margin>
            <input {...getInputProps()} />
            <MessageInput
              text={msgText}
              onChange={handleChangeMsgText}
              onSelect={handleSelectEmoji}
              isNewThread
              placeholder={t('newThreadModal.placeholder')}
              file={msgFile}
              fileURL={fileUrl}
              opened={isOpenDialog}
              disabled={disabled}
              onOpen={handleOpenAttach}
              onRemove={handleClearInput}
            />
          </ModalDetailWrapper>
        </ModalBodyWrapper>
      )}
    </MangoModal>
  )
}

export default NewThreadModal
